import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import config from '@config';

@Injectable({ providedIn: 'root' })
export class UrlService {
  readonly location = inject(Location);

  isLogin() {
    return this.location.path().startsWith('/login') || this.location.path().startsWith('/agreement');
  }

  isRoot() {
    return this.location.path() === '';
  }

  COPAY_ACTIONS_URL = `${config.codeRequestUrl}/ccr_action/{tenant}`;
  ETL_URL = `${config.etlUrl}/`;
  DATA_DELIVERY_DESTINATIONS_URL = `${config.dataDeliveryUrl}/destinations/GLD`;
  DATA_DELIVERY_DELIVERIES_URL = `${config.dataDeliveryUrl}/sequences/GLD/search`;
  DATA_DELIVERY_DOWNLOAD_URL = `${config.dataDeliveryUrl}/data-download/GLD`;
  PAYMENT_URL = `${config.paymentUrl}/`;
  TABLE_EDITOR_SERVICE_URL = `${config.tableEditorServiceUrl}/`;
  SUPPORT_REQUESTS_URL = `${config.supportUrl}/support_requests`;
  SUPPORT_REQUESTS_NO_AUTH_URL = `${config.supportUrl}/support_requests/login_page`;
  MASTER_DATA_URL = config.masterDataUrl;
  PA_SERVICE = config.paUrl;
  TOKEN_ACTION_CREATE_URL = `${config.tokenActionUrl}/ta`;
  LOOKUPS_URL = `${config.masterDataUrl}/settings/{subject}/{attribute}`;
  LOOKUPS_LIST_URL = `${config.masterDataUrl}/settings/{subject}`;
  DATAVIEW_VIEWS_URL = `${config.masterDataUrl}/dataview/{page}/views`;
  AGGREGATES_URL = `${config.aggregatesUrl}/`;
  DEV_OPS_SERVICE_BASE_URL = config.devOpsUrl;
  CONDITION_EXPRESSION_RESOLVER_URL = `${config.copayUrl}/resolve-condition-expression`;
  CLIENT_IP_URL = `${config.masterDataUrl}/client/ip`;
  CHANGE_LOGS_URL = `${config.masterDataUrl}/change-logs`;
  AUTH_SERVICE_URL = `${config.authUrl}/auth`;
  USERS_URL = `${config.authUrl}/users`;
  USERS_ROLES_URL = `${config.authUrl}/roles`;
  DYNAMO_DB_URL = `${config.dataMaintenanceUrl}/dynamo-db`;
  OPEN_PHONE_LOG_URL = `${config.paUrl}/open-phone-log`;
  NCPDP_MESSAGES_SEARCH_URL = `${config.paUrl}/ncpdp-messages/search`;
  NCPDP_TESTING_URL = `${config.ncpdpUrl}/testing/ccr_request_dict/ccr_response_dict`;
  PAYOR_INFO_BY_TENANT_URL = `${config.masterDataUrl}/payor-info/by-tenant/{tenant}`;
  PATIENT_BY_ID_URL = `${config.masterDataUrl}/patients/{id}`;
  PATIENTS_BY_IDS_URL = `${config.masterDataUrl}/patients/by-ids`;
  MASTER_DATA_PRESCRIBERS_URL = `${config.masterDataUrl}/prescribers`;
  MASTER_DATA_PHARMACIES_URL = `${config.masterDataUrl}/pharmacies`;
  MASTER_DATA_PHARMACY_CHAINS_URL = `${config.masterDataUrl}/pharmacy-chains`;
  PA_PHARMACY_URL = `${config.paUrl}/pharmacy`;
  PA_PHARMACY_BY_NPI_URL = `${config.paUrl}/pharmacy-by-npi`;
  MASTER_DATA_PAYOR_MASTER_LOOKUPS_URL = `${config.masterDataUrl}/payor-master-lookups`;
  MASTER_DATA_PAYOR_ALLOWED_BINS_URL = `${config.masterDataUrl}/payor-allowed-bins`;
  MASTER_DATA_PRECISION_PAYOR_SPINES_URL = `${config.masterDataUrl}/precision-payor-spines`;
  MASTER_DATA_CAW_PBM_ID_MAPPINGS_URL = `${config.masterDataUrl}/caw-pbm-id-mappings`;
  PRODUCT_PROGRAM_URL = `${config.paUrl}/product-programs`;
  PRODUCT_URL = `${config.masterDataUrl}/products`;
  RX_TRANSACTION_SETTINGS_GET_PARAMETERS_URL = `${config.rxTransactionSettingsUrl}/parameters`;
  FEATURE_FLAGS_URL = `${config.masterDataUrl}/feature-flags`;
  FEATURE_FLAGS_PUBLIC_URL = `${config.masterDataUrl}/feature-flags/public`;
  FEATURE_FLAGS_CONTEXT_URL = `${config.masterDataUrl}/feature-flags/context`;
  FEATURE_FLAGS_DEFINITIONS_URL = `${config.masterDataUrl}/feature-flags/definitions`;
  CLAIM_RAW_RECORD_URL = `${config.paUrl}/claims/{tenant}/{ncpdp}/{created}`;

  AGGREGATES_SERVICE = {
    claimsStats: 'patient-assistance/requests/stats',
    paidClaimsStats: 'patient-assistance/paid-claim/stats',
    paidClaimSumStats: 'patient-assistance/paid-claim/sum-stats',
    paidClaimAgingBuckets: 'patient-assistance/paid-claim/aging-buckets',
    patientAssistanceRequestsHistory: 'patient-assistance/requests/history',
    patientAssistanceRequestsSystem: 'patient-assistance/requests/system',
    pharmacyScorecardUrl: 'pharmacy/scorecard',
    pharmacyScorecardPerformance: 'pharmacy/scorecard/performance',
    pharmacyScorecardHistory: 'pharmacy/scorecard/history',
    pharmacyScorecardNetworkHistory: 'pharmacy/scorecard/network-history',
    pharmacyScorecardLatestClaims: 'pharmacy/scorecard/claims',
    pharmacyScorecardTopPrescribers: 'pharmacy/scorecard/prescribers',
    pharmacyScorecardTopPlans: 'pharmacy/scorecard/plans',
    pharmacyRefillRatio: 'pharmacy/scorecard/refill-ratio',
    statementsSumStats: 'statements/sum-stats',
    openPhoneLogSumStats: 'open-phone-log/sum-stats',
    businessFlowCaseCustomerSupportSumStats: 'business-flow-case/customer-support-sum-stats',
    businessFlowCaseBVerificationSumStats: 'business-flow-case/b-verification-sum-stats',
    trendsByProductNdc: 'trends/{productNdc}',
    anomaliesByAnomalyType: 'anomalies/{anomalyType}',
    refillTrend: 'patients/refill-trend?firstSeenFrom={from}&firstSeenTo={to}',
  };

  TABLE_EDITOR_SERVICE = {
    tableSchemas: 'table-schemas/{tenant}',
    getTableData: 'mappings/table/{tableName}?tableSchemaVersion={tableSchemaVersion}' + '&version={version}&tenant={tenant}',
    getTableDataChanges:
      'mappings/table/{tableName}/changes/{startDate}/{endDate}?tableSchemaVersion={tableSchemaVersion}' + '&limit={limit}&tenant={tenant}',
    exportTableData: 'export/mappings/table/{tableName}/versions/{version}?tableSchemaVersion={tableSchemaVersion}&tenant={tenant}',
    exportTableDataChanges:
      'export/mappings/table/{tableName}/changes/{startDate}/{endDate}?tableSchemaVersion={tableSchemaVersion}' +
      '&limit={limit}&tenant={tenant}',
    getTableItem: 'mappings/table/{tableName}/{itemId}/versions/{version}?tableSchemaVersion={tableSchemaVersion}&tenant={tenant}',
    getTableItemVersions: 'mappings/table/{tableName}/{itemId}/versions?tableSchemaVersion={tableSchemaVersion}&tenant={tenant}',
    postTableData:
      'mappings/table/{tableName}?tableSchemaVersion={tableSchemaVersion}&versionGroup={versionGroup}' +
      '&version={version}&tenant={tenant}',
    discardTableItem:
      'mappings/discard/table/{tableName}?tableSchemaVersion={tableSchemaVersion}&versionGroup={versionGroup}' +
      '&version={version}&tenant={tenant}',
    versions: 'versions/{group}?tenant={tenant}',
    versionsCreate: 'versions/open/{group}?tenant={tenant}',
    versionsPublish: 'versions/publish/{versionId}?versionGroup={versionGroup}&tenant={tenant}',
    versionsDiscardChanges: 'versions/discard-changes/{version}?versionGroup={versionGroup}&tenant={tenant}',
    exportAllTablesChanges: 'export/mappings/version-groups/{versionGroup}/changes?tenant={tenant}&tableSchemaVersion={tableSchemaVersion}',
    rollbackChanges: 'mappings/rollback',
  };
}
